import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { SuperAdminActions, SuperAdminTypes } from 'ducks/super-client';
import { selectSuperClientConfirmation } from 'selectors/super-admin';
import { fjModalSizes, FjEllipsis } from 'components/primitives';
import { Modal } from 'components/styled-primitives';
import { pluralize } from 'utils';

interface SuperClientConfirmation {
  type: string;
  visible: boolean;
  failed: boolean;
  actions: string[];
}

// Shows success/failure messages in response to `SuperClient` HTTP actions
const LicenseConfirmationModal = (): React.ReactElement | null => {
  const confirmation = useSelector(selectSuperClientConfirmation) as SuperClientConfirmation;
  const dispatch = useDispatch();
  const hideModal = (): void => {
    dispatch(SuperAdminActions.hideLicenseModal());
  };

  const renderFailedActionsList = (failedActions: string[]): React.ReactElement | null => {
    const actionCount = failedActions.length;
    if (!actionCount) {
      return null;
    }

    return (
      <div>
        <p>Failed {pluralize(actionCount, 'action')}:</p>
        {failedActions.map((action) => (
          <div key={action} className="display-flex">
            <FjEllipsis>
              <div>{action}</div>
            </FjEllipsis>
          </div>
        ))}
      </div>
    );
  };

  const renderLicenseCreationSuccess = (): React.ReactElement => (
    <Modal
      accept={{
        text: 'OK',
        callback: hideModal,
      }}
      centerContent
      size={fjModalSizes.XS}
      title="License created!"
    />
  );

  const renderLicenseCreationFailure = (): React.ReactElement => (
    <Modal
      accept={{
        text: 'OK',
        callback: hideModal,
      }}
      title="Failed to create license"
    >
      {renderFailedActionsList(confirmation.actions)}
    </Modal>
  );

  const renderLicenseManageSuccess = (): React.ReactElement => (
    <Modal
      accept={{
        text: 'OK',
        callback: hideModal,
      }}
      title="License updated!"
      size={fjModalSizes.XS}
    />
  );

  const renderLicenseManageFailure = (): React.ReactElement => (
    <Modal
      accept={{
        text: 'OK',
        callback: hideModal,
      }}
      title="Failed to edit license"
    >
      {renderFailedActionsList(confirmation.actions)}
    </Modal>
  );

  if (!confirmation.visible) {
    return null;
  }

  // TODO handle confirmations from manage/edit
  if (confirmation.type === SuperAdminTypes.CREATE_LICENSE) {
    if (confirmation.failed) {
      return renderLicenseCreationFailure();
    }
    return renderLicenseCreationSuccess();
  }
  if (confirmation.type === SuperAdminTypes.MANAGE_LICENSE) {
    if (confirmation.failed) {
      return renderLicenseManageFailure();
    }
    return renderLicenseManageSuccess();
  }

  return null;
};

export default LicenseConfirmationModal;
